import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TreeList,
  Selection
} from 'devextreme-react/tree-list';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Button, SelectBox, TextBox } from 'devextreme-react';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { Column } from 'devextreme-react/data-grid';
import { formatMessage } from 'devextreme/localization';
import { columnTreeList } from './columnTreeList';
import PopupMVP from '../../PopupMVP';

const initialPopupData = {
  description: '',
  stockConfigCode: '',
  parent: '',
  parentType: '',
  holderId: '',
  sapCode: '',
  stockConfigNumId: '',
  currentStockId: ''
};

const StockConfigure = ({
  plantId,
  stockConfigureReducer,
  getStockConfigureList,
  addNewStockLevel,
  deleteStockLevel
}) => {
  const treeList = useRef(null);
  const [activeOnly] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [popupData, setPopupData] = useState(initialPopupData);
  const [addPopupData, setAddPopupData] = useState(initialPopupData);
  const [visibleModal, setVisibleModal] = useState({
    add: false,
    edit: false,
    delete: false
  });

  // Стейт для хранения информации какой тип уровня сейчас добавляется
  // Значения: 'area' | 'box'
  const [addMode, setAddMode] = useState('area');

  // Получение дерева складов
  useEffect(() => {
    if (plantId) {
      getStockConfigureList({
        plantId,
        activeOnly
      });
    }
  }, [activeOnly, getStockConfigureList, plantId]);

  useEffect(() => {
    if (stockConfigureReducer.loading) {
      treeList.current.instance.beginCustomLoading();
    } else {
      treeList.current.instance.endCustomLoading();
    }
  }, [stockConfigureReducer.loading, treeList]);

  const onOptionChanged = useCallback((e) => {
    if (e.name === 'expandedRowKeys') {
      setExpandedRowKeys(e.value);
    }
  }, []);

  // Меняем режим в зависимости от уровня выбранного элемента
  useEffect(() => {
    if (addPopupData && addPopupData.parentType) {
      if (addPopupData.parentType === 10) {
        setAddMode('area');
      }
      if (addPopupData.parentType === 20) {
        setAddMode('box');
      }
    }
  }, [addPopupData]);

  // Выбор элемента из дерева
  const onSelectionChanged = useCallback(({ selectedRowsData }) => {
    if (selectedRowsData.length) {
      setSelectedRow(selectedRowsData[0]);
      // Находим id склада для элемента
      const currentStockId = stockConfigureReducer.listStocks
        .find((item) =>
          item.stockConfigNumId
          === selectedRowsData[0].stockConfigParentNumId)?.stockConfigParentNumId;
      setPopupData({
        description: selectedRowsData[0].description,
        stockConfigCode: selectedRowsData[0].stockConfigCode === null ? '' : selectedRowsData[0].stockConfigCode,
        parent: selectedRowsData[0].stockConfigParentNumId,
        stockConfigNumId: selectedRowsData[0].stockConfigNumId,
        parentType: selectedRowsData[0].stockLevelTypeNumId,
        currentStockId
      });
      setAddPopupData((prevState) => ({
        ...prevState,
        parent: selectedRowsData[0].stockConfigNumId,
        parentType: selectedRowsData[0].stockLevelTypeNumId
      }));
    } else {
      setSelectedRow(null);
      setPopupData(initialPopupData);
    }
  }, [stockConfigureReducer.listStocks]);

  const buttonExpand = () => (
    <Button
      text={expanded ? formatMessage('stockConfigure').collapseAll : formatMessage('stockConfigure').expandAll}
      onClick={() => {
        setExpanded((prevState) => !prevState);
        setExpandedRowKeys([]);
      }}
    />
  );

  const buttonAdd = useMemo(() => () => (
    <Button
      text={addMode === 'area' ? formatMessage('stockConfigure').addArea : formatMessage('stockConfigure').addBox}
      onClick={() => setVisibleModal((prevState) => ({ ...prevState, add: true }))}
      visible={!!selectedRow && addPopupData.parentType !== 30}
    />
  ), [addMode, selectedRow, addPopupData.parentType]);

  const buttonDelete = () => (
    <Button
      text={formatMessage('stockConfigure').deleteLevel}
      onClick={() => setVisibleModal((prevState) => ({ ...prevState, delete: true }))}
      disabled={!selectedRow || popupData.parentType === 10}
    />
  );

  const buttonEdit = () => (
    <Button
      text={formatMessage('stockConfigure').editLevel}
      onClick={() => setVisibleModal((prevState) => ({ ...prevState, edit: true }))}
      disabled={!selectedRow}
    />
  );

  /** Получаем выпадающий список для выбора родителя */
  const getParentSelectData = useMemo(() => {
    if (stockConfigureReducer.listStocks.length) {
      // Добавление уровня
      if (visibleModal.add) {
        // Зону можно добавить только для конкретного склада
        if (addMode === 'area') {
          return stockConfigureReducer.listStocks
            .filter((item) =>
              item.stockLevelTypeNumId === 10 && item.stockConfigNumId === addPopupData.parent)
            .map((item) =>
              ({ description: item.description, stockConfigNumId: item.stockConfigNumId }));
        }
        // Бокс можно добавить только для конкретной зоны
        if (addMode === 'box') {
          return stockConfigureReducer.listStocks
            .filter((item) =>
              item.stockLevelTypeNumId === 20 && item.stockConfigNumId === addPopupData.parent)
            .map((item) =>
              ({ description: item.description, stockConfigNumId: item.stockConfigNumId }));
        }
        // Редактирование уровня
      } else {
        // При редактировании склада нельзя поставить его на уровни ниже
        if (popupData.parentType === 10) {
          return [{ description: formatMessage('stockConfigure').noParentDesc, stockConfigNumId: null }]
        }
        // При редактировании зоны нельзя изменить склад
        if (popupData.parentType === 20) {
          return stockConfigureReducer.listStocks
            .filter((item) =>
              item.stockLevelTypeNumId === 10 && item.stockConfigNumId === popupData.parent)
            .map((item) =>
              ({ description: item.description, stockConfigNumId: item.stockConfigNumId }));
        }
        /** При редактировании бокса можно изменить склад
         * в рамках одного склада. Для определения принадлежности
         * зон к одному складу используем поле currentStockId */
        if (popupData.parentType === 30) {
          return stockConfigureReducer.listStocks
            .filter((item) =>
              item.stockLevelTypeNumId === 20)
            .filter((item) =>
              item.stockConfigParentNumId === popupData.currentStockId)
            .map((item) =>
              ({ description: item.description, stockConfigNumId: item.stockConfigNumId }));
        }
      }
    }

    return [];
  }, [
    addPopupData.parent,
    stockConfigureReducer.listStocks,
    visibleModal.add,
    popupData.currentStockId,
    popupData.parentType,
    popupData.parent,
    addMode
  ]);

  // Добавление нового уровня в дерево
  const addNewLevel = useCallback(() => {
    addNewStockLevel({
      description: addPopupData.description,
      plantConfigNumId: plantId,
      stockConfigParentNumId: addPopupData.parent,
      stockConfigCode: addPopupData.stockConfigCode
    });
  }, [addNewStockLevel, addPopupData, plantId]);

  // Редактирование уровня
  const editLevel = useCallback(() => {
    addNewStockLevel({
      description: popupData.description,
      plantConfigNumId: plantId,
      stockConfigParentNumId: popupData.parent,
      stockConfigCode: popupData.stockConfigCode,
      sapCode: popupData.sapCode,
      holderId: popupData.holderId,
      stockConfigNumId: popupData.stockConfigNumId
    });
  }, [popupData, addNewStockLevel, plantId]);

  // Удаление уровня
  const deleteLevel = useCallback(() => {
    deleteStockLevel({
      stockId: popupData.stockConfigNumId
    });
  }, [deleteStockLevel, popupData.stockConfigNumId]);

  const onHidingAddModal = useCallback(() => {
    setVisibleModal((prevState) => ({ ...prevState, add: false }));
    setAddPopupData((prevState) => ({
      ...prevState,
      description: initialPopupData.description,
      holderId: initialPopupData.holderId,
      sapCode: initialPopupData.sapCode
    }));
  }, []);

  const onHidingDeleteModal = useCallback(() => {
    setVisibleModal((prevState) => ({ ...prevState, delete: false }));
  }, []);

  const onHidingEditModal = useCallback(() => {
    setVisibleModal((prevState) => ({ ...prevState, edit: false }));
  }, []);

  // Обновление дерева после изменений
  useEffect(() => {
    if ((stockConfigureReducer.add.okAdd || stockConfigureReducer.delete.okDelete) && plantId) {
      getStockConfigureList({
        plantId,
        activeOnly
      });
      onHidingAddModal();
      onHidingEditModal();
      onHidingDeleteModal();
    }
  }, [
    activeOnly,
    getStockConfigureList,
    plantId,
    stockConfigureReducer.add.okAdd,
    stockConfigureReducer.delete.okDelete,
    onHidingAddModal,
    onHidingEditModal,
    onHidingDeleteModal
  ]);

  // Попап добавления уровня
  const renderPopupAdd = useMemo(() => (
    <PopupMVP
        visible={visibleModal.add}
        onHiding={onHidingAddModal}
        title={addMode === 'area' ? formatMessage('stockConfigure').addArea : formatMessage('stockConfigure').addBox}
      >
      <div className="formData">
        <div className="optionsRow">
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').description}</span>
            <TextBox
              value={addPopupData.description}
              onValueChanged={({ value }) => {
                setAddPopupData((prevState) =>
                  ({ ...prevState, description: value }));
              }}
              isValid={!!addPopupData.description} />
          </div>
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').configCode}</span>
            <TextBox
              value={addPopupData.stockConfigCode}
              onValueChanged={({ value }) => {
                setAddPopupData((prevState) =>
                  ({ ...prevState, stockConfigCode: value }));
              }}
            />
          </div>
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').parentLevel}</span>
            <SelectBox stylingMode="outlined"
                       items={getParentSelectData}
                       displayExpr="description"
                       valueExpr="stockConfigNumId"
                       value={addPopupData.parent}
                       onValueChanged={({ value }) => {
                         setAddPopupData((prevState) => ({
                           ...prevState,
                           parent: value
                         }));
                       }}
                       isValid={addPopupData.parent !== ''}
            />
          </div>
        </div>
        <div className="formButton">
          <Button
            text={formatMessage('textButton').cancel}
            type="danger"
            stylingMode="text"
            onClick={onHidingAddModal}
          />
          <Button
            type="default"
            stylingMode="contained"
            width={200}
            onClick={addNewLevel}
            disabled={addPopupData.parent === '' || !addPopupData.description}
          >
            <span>{formatMessage('textButton').confirm}</span>
            <LoadIndicator className="indicator" visible={stockConfigureReducer.add.loading} />
          </Button>
        </div>
      </div>
    </PopupMVP>
  ), [
    visibleModal.add,
    onHidingAddModal,
    addPopupData.description,
    addPopupData.stockConfigCode,
    addPopupData.parent,
    getParentSelectData,
    stockConfigureReducer.add.loading,
    addNewLevel,
    addMode
  ]);

  // Попап редактирования уровня
  const renderPopupEdit = useMemo(() => (
    <PopupMVP
      visible={visibleModal.edit}
      onHiding={onHidingEditModal}
      title={formatMessage('stockConfigure').editLevel}
    >
      <div className="formData">
        <div className="optionsRow">
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').description}</span>
            <TextBox
              value={popupData.description}
              onValueChanged={({ value }) => {
                setPopupData((prevState) =>
                  ({ ...prevState, description: value }));
              }}
              isValid={!!popupData.description} />
          </div>
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').configCode}</span>
            <TextBox
              value={popupData.stockConfigCode}
              onValueChanged={({ value }) => {
                setPopupData((prevState) =>
                  ({ ...prevState, stockConfigCode: value }));
              }}
            />
          </div>
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').holderId}</span>
            <TextBox
              value={popupData.holderId}
              onValueChanged={({ value }) => {
                setPopupData((prevState) =>
                  ({ ...prevState, holderId: value }));
              }}
            />
          </div>
        </div>
        <div className="optionsRow">
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').sapCode}</span>
            <TextBox
              value={popupData.sapCode}
              onValueChanged={({ value }) => {
                setPopupData((prevState) =>
                  ({ ...prevState, sapCode: value }));
              }}
            />
          </div>
          <div className="optionsData">
            <span>{formatMessage('stockConfigure').parentLevel}</span>
            <SelectBox stylingMode="outlined"
                       items={getParentSelectData}
                       displayExpr="description"
                       valueExpr="stockConfigNumId"
                       value={popupData.parent}
                       onValueChanged={({ value }) => {
                         setPopupData((prevState) => ({
                           ...prevState,
                           parent: value
                         }));
                       }}
                       isValid={popupData.parent !== ''}
            />
          </div>
        </div>
        <div className="formButton">
          <Button
            text={formatMessage('textButton').cancel}
            type="danger"
            stylingMode="text"
            onClick={onHidingEditModal}
          />
          <Button
            type="default"
            stylingMode="contained"
            width={200}
            onClick={editLevel}
            disabled={popupData.parent === '' || !popupData.description}
          >
            <span>{formatMessage('textButton').confirm}</span>
            <LoadIndicator className="indicator" visible={stockConfigureReducer.add.loading} />
          </Button>
        </div>
      </div>
    </PopupMVP>
  ), [
    editLevel,
    getParentSelectData,
    onHidingEditModal,
    popupData,
    stockConfigureReducer.add.loading,
    visibleModal.edit
  ]);

  // Попап удаления уровня
  const renderPopupDelete = useMemo(() => (
    <PopupMVP
      visible={visibleModal.delete}
      onHiding={onHidingDeleteModal}
      title={formatMessage('stockConfigure').deleteLevel}
    >
      <div>
        <div className="optionsRow">
          {`${formatMessage('stockConfigure').deleteConfirm} ${popupData.description}?`}
        </div>
      </div>
      <div className="formButton">
        <Button
          text={formatMessage('textButton').cancel}
          type="danger"
          stylingMode="text"
          onClick={onHidingDeleteModal}
        />
        <Button
          type="default"
          stylingMode="contained"
          width={200}
          onClick={deleteLevel}
        >
          <span>{formatMessage('textButton').confirm}</span>
          <LoadIndicator className="indicator" visible={stockConfigureReducer.delete.loading} />
        </Button>
      </div>
    </PopupMVP>
  ), [
    onHidingDeleteModal,
    visibleModal.delete,
    popupData.description,
    deleteLevel,
    stockConfigureReducer.delete.loading
  ]);

  return (
    <div>
      <Toolbar>
        <Item location="before" render={buttonExpand} />
        <Item location="after" render={buttonAdd} />
        <Item location="after" render={buttonEdit} />
        <Item location="after" render={buttonDelete} />
      </Toolbar>
      <TreeList
        ref={treeList}
        id="stockConfigure"
        dataSource={stockConfigureReducer.listStocks}
        rootValue={null}
        keyExpr="stockConfigNumId"
        parentIdExpr="stockConfigParentNumId"
        autoExpandAll={expanded}
        expandedRowKeys={expandedRowKeys}
        onSelectionChanged={onSelectionChanged}
        onOptionChanged={onOptionChanged}
      >
        <Selection mode="single"
                   allowSelectAll={false}
        />
        {columnTreeList.map((item) => (
          <Column dataField={item.dataField}
                  key={item.dataField}
                  caption={item.caption}
                  visible
                  width={item.width}
                  cellRender={item.cellRender}
                  dataType={item.dataType}
          />
        ))}
      </TreeList>
      {renderPopupAdd}
      {renderPopupEdit}
      {renderPopupDelete}
    </div>
  );
};

export default StockConfigure;
